import { type EChartsTheme, type EChartsInitOpts } from 'echarts';
import type { SetOptionOpts } from 'echarts/lib/core/echarts';
import { util, init as echartsInit } from 'echarts/core';
import { version } from '../../package.json';
import EventHandler from './EventHandler';
import Model, { type ThemeConfig } from './Model';
import { debug } from '../util/debug';
import type { Option } from './option';

type ECharts = ReturnType<typeof echartsInit>;

type EnhancedECharts = ECharts & {
  // eslint-disable-next-line no-use-before-define
  $dvGetOwner: () => StandardChart;
};

// eslint-disable-next-line no-use-before-define
const instances: { [id: string]: StandardChart } = {};

// eslint-disable-next-line no-use-before-define
function connectStandardChartToECharts(echartsIns: ECharts, StandardChartIns: StandardChart) {
  echartsIns.$dvGetOwner = () => StandardChartIns;
}

export function getStandardChart(echartsIns: ECharts) {
  return echartsIns.$dvGetOwner();
}

// eslint-disable-next-line no-use-before-define
export function setStandardChartIns(chart: StandardChart) {
  instances[chart.id] = chart;
}

export function getStandardChartInsById(id: string) {
  return instances[id];
}

export default class StandardChart extends EventHandler {
  /** 埋点统计的ID */
  protected readonly __statisticId = 'thsc-datav-standard-chart';

  /**
   * @readonly
   */
  id: string;

  protected _$dom: HTMLElement;

  private __ecTheme: EChartsTheme;

  private __initOption: EChartsInitOpts;

  private __echarts: EnhancedECharts;

  /** 配置项管理 */
  protected _model: Model;

  protected _initialized: boolean;

  private __pendingCallback: (() => void)[];

  private __playOption: { opts?: SetOptionOpts } = {};

  private __timersMap: Map<string, ReturnType<typeof setTimeout>[]> = new Map();

  /**
   * 构造函数
   * * 该阶段只完成所有变量初始化和组件的实例化，不执行任何繁重的计算逻辑
   */
  constructor(
    dom: HTMLElement,
    theme?: ThemeConfig,
    ecTheme?: EChartsTheme | EChartsInitOpts,
    initOption?: EChartsInitOpts
  ) {
    super();
    debug('new Chart: %o', this);

    this._$dom = dom;
    this._$dom.style.userSelect = 'none';

    if (typeof initOption === 'undefined') {
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, no-param-reassign
      initOption = ecTheme;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, no-param-reassign
      ecTheme = null;
    }

    this.__ecTheme = ecTheme;
    this.__initOption = util.clone(initOption);

    this._model = new Model(theme);

    this.__pendingCallback = [];
    this._initialized = false;
    // * Why? see: https://nodejs.org/en/docs/guides/event-loop-timers-and-nexttick/#why-use-process-nexttick
    setTimeout(() => {
      this._initialize();
      this._initialized = true;

      this.__pendingCallback.forEach(cb => cb());
      this.__pendingCallback = [];
    }, 0);

    StandardChart.__statistic(this.__statisticId);
  }

  getDom() {
    return this._$dom;
  }

  getECharts() {
    return this.__echarts;
  }

  getModel() {
    return this._model;
  }

  clearTimers(key?: string) {
    let timers = this.__timersMap.get(key) || [];

    if (typeof key === 'undefined') {
      timers = Array.from(this.__timersMap.values()).flat();
      this.__timersMap.clear();
    } else {
      this.__timersMap.delete(key);
    }

    timers.forEach(timer => clearTimeout(timer));
  }

  cacheTimers(key: string, timers: ReturnType<typeof setTimeout>[]) {
    if (typeof key === 'undefined') {
      return;
    }

    this.clearTimers(key);
    this.__timersMap.set(key, timers);
  }

  getTimers(key: string) {
    return this.__timersMap.get(key) || [];
  }

  protected _initialize() {
    debug('dv:beforeinit');
    const self = this;
    self.dispatchEvent('dv:beforeinit');

    // @ts-ignore
    // * 便于线上调调试
    self._$dom.__$$getStandardChartIns = () => self;

    // @ts-ignore
    self.__echarts = echartsInit(self._$dom, this.__ecTheme, this.__initOption);

    // * 关联 ECharts 实例与 StandardChart 实例
    connectStandardChartToECharts(self.__echarts, self);

    self._flushPendingToRegisterEChartsInsEvents();
    self._bindEvents();

    self.dispatchEvent('dv:afterinit');
    debug('dv:afterinit');
  }

  /**
   * 销毁实例
   * * 释放资源引用，避免内存泄露
   */
  destroy() {
    if (!this._initialized) {
      return;
    }
    debug('dv:destroy...');
    this.dispatchEvent('dv:beforedestroy');

    delete this._$dom.__$$getStandardChartIns;

    this.clearTimers();
    this.__echarts.dispose();
    this.__echarts = null;
    this._initialized = false;
    delete instances[this.id];

    this.dispatchEvent('dv:destroy');

    this.dispatchEvent('dv:afterdestroy');
  }

  // 动画 -----------------------------------------------------------------

  play(arg: { option: Option } & this['__playOption']) {
    if (!this._initialized) {
      this.__pendingCallback.push(() => {
        this.play(arg);
      });

      return this;
    }
    this.dispatchEvent('dv:play');

    const { option, ...playOption } = arg;
    if (typeof option === 'undefined') {
      throw new Error(`"option" is required.`);
    }
    this.__playOption = playOption;

    this._model.update(option);
    this.__update();

    return this;
  }

  private __update() {
    debug('dv:beforeupdate');
    this.dispatchEvent('dv:beforeupdate');

    this._model.prepare(this);
    const option = this._model.getOption();

    this.__echarts.setOption(option, this.__playOption.opts);

    this.dispatchEvent('dv:afterupdate');
    debug('dv:afterupdate: %o', option);
  }

  /**
   * 发送埋点
   */
  private static __statistic(statisticId: string) {
    // if (!this.track) {
    //   return false;
    // }

    if (window._thsclog) {
      // 总量
      window._thsclog({
        name: statisticId,
        ver: 'all'
      });
      // 单版本量
      window._thsclog({
        name: statisticId,
        ver: version
      });
    } else {
      console.warn(
        'Burial point required, please add JS dependency <script src="//s.thsi.cn/js/ta.min.js"></script>'
      );
    }

    return null;
  }
}
